import React, { useState, useEffect, useRef } from "react";
import "./RecWiseCode.css";
import { useNavigate } from 'react-router-dom';
import CTA from './CTA';

const PerformanceManagementModule = () => {
  const [activeSection, setActiveSection] = useState("goal-setting");
  const [visibleSections, setVisibleSections] = useState(new Set());
  const sectionsRef = useRef({});
  const navigate = useNavigate();
  const handlePerformance = () => navigate('/access-recruit');

  const sectionContent = {
    "goal-setting": {
      title: "Strategic Goal Setting",
      description: "Align individual objectives with organizational strategy through cascading goal frameworks. Create SMART goals at company, department, team, and individual levels with clear KPIs and success metrics. Track goal progress in real-time with automated updates and milestone tracking. Facilitate regular goal check-ins and adjustments while maintaining visibility into how individual contributions drive overall business success."
    },
    "performance-reviews": {
      title: "Structured Perform Reviews",
      description: "Conduct comprehensive performance evaluations with customizable review cycles and rating frameworks. Implement 360-degree feedback mechanisms incorporating input from managers, peers, direct reports, and self-assessments. Schedule automated review periods with reminders and track completion rates. Store historical performance data for trend analysis and provide managers with tools for delivering constructive, actionable feedback during review discussions."
    },
    "competency-framework": {
      title: "Competency & Assessment",
      description: "Define and measure core competencies required for success across roles and levels. Create competency frameworks tied to organizational values and strategic priorities. Assess current skill levels, identify development gaps, and track competency progression over time. Use competency data for talent planning, succession management, and personalized development path creation that addresses specific skill enhancement needs."
    },
    "continuous-feedback": {
      title: "Continuous Feedback ",
      description: "Foster a culture of ongoing feedback with real-time recognition and coaching tools. Enable peer-to-peer recognition, manager feedback, and milestone celebrations. Capture informal feedback throughout the year to complement formal review cycles. Provide feedback templates, conversation guides, and development suggestions to support constructive dialogue and reinforce positive behaviors aligned with organizational values."
    },
    "development-planning": {
      title: "Development Planning",
      description: "Create personalized development plans that address performance gaps and career aspirations. Identify training needs, stretch assignments, mentoring opportunities, and skill-building activities. Track development plan progress and measure impact on performance improvement. Align development activities with career paths and succession planning initiatives to support both current role effectiveness and future growth potential."
    }
  };

  const sections = [
    { id: "goal-setting", label: "GOAL SETTING" },
    { id: "performance-reviews", label: "REVIEWS" },
    { id: "competency-framework", label: "COMPETENCIES" },
    { id: "continuous-feedback", label: "FEEDBACK" },
    { id: "development-planning", label: "DEVELOPMENT" }
  ];

  useEffect(() => {
    const onScroll = () => {
      const pos = window.scrollY + 150;
      for (const sec of sections) {
        const el = sectionsRef.current[sec.id];
        if (!el) continue;
        if (pos >= el.offsetTop && pos < el.offsetTop + el.offsetHeight) {
          setActiveSection(sec.id);
          break;
        }
      }
    };
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((en) => {
          if (en.isIntersecting) {
            setVisibleSections((prev) => new Set([...prev, en.target.dataset.section]));
          }
        });
      },
      { threshold: 0.15 }
    );
    Object.values(sectionsRef.current).forEach((el) => el && obs.observe(el));
    return () => obs.disconnect();
  }, []);

  const scrollToSection = (id) => {
    const el = sectionsRef.current[id];
    if (el) window.scrollTo({ top: el.offsetTop - 100, behavior: "smooth" });
  };

  return (
    <div className="resto-main-wrapper">
      {/* <nav className="resto-sticky-navbar">
        <div className="resto-nav-container">
          {sections.map((s) => (
            <button
              key={s.id}
              className={`resto-nav-button ${activeSection === s.id ? "resto-active" : ""}`}
              onClick={() => scrollToSection(s.id)}
            >
              {s.label}
            </button>
          ))}
        </div>
      </nav> */}

      <div className="resto-content-wrapper">

        {/* 1. Goal Setting */}
        <section
          ref={(el) => (sectionsRef.current["goal-setting"] = el)}
          data-section="goal-setting"
          className={`resto-section ${visibleSections.has("goal-setting") ? "resto-visible" : ""}`}
        >
          <div className="resto-section-content resto-fade-in-left">
            <div className="resto-section-label">Goal Management</div>
            <h2 className="resto-section-title">
              {sectionContent["goal-setting"].title}
            </h2>
            <p className="resto-section-description">
              {sectionContent["goal-setting"].description}
            </p>

            <div className="resto-buttons-group">
              <button className="resto-primary-btn" onClick={handlePerformance}>Set Goals</button>
              <button className="resto-secondary-btn" onClick={handlePerformance}>Track Progress</button>
            </div>
          </div>

          <div className="resto-section-image resto-fade-in-right">
            <img src="/images/prm1.png" className="resto-real-image" alt="Goal Setting" />
          </div>
        </section>

        {/* 2. Performance Reviews */}
        <section
          ref={(el) => (sectionsRef.current["performance-reviews"] = el)}
          data-section="performance-reviews"
          className={`resto-section resto-reverse ${visibleSections.has("performance-reviews") ? "resto-visible" : ""}`}
        >
          <div className="resto-section-content resto-fade-in-right">
            <div className="resto-section-label">Reviews</div>
            <h2 className="resto-section-title">
              {sectionContent["performance-reviews"].title}
            </h2>
            <p className="resto-section-description">
              {sectionContent["performance-reviews"].description}
            </p>

            <div className="resto-buttons-group">
              <button className="resto-primary-btn" onClick={handlePerformance}>Schedule Reviews</button>
              <button className="resto-secondary-btn" onClick={handlePerformance}>Conduct 360°</button>
            </div>
          </div>

          <div className="resto-section-image resto-fade-in-left">
            <img src="/images/prm2.png" className="resto-real-image" alt="Performance Reviews" />
          </div>
        </section>

        <CTA/>

        {/* 3. Competency Framework */}
        <section
          ref={(el) => (sectionsRef.current["competency-framework"] = el)}
          data-section="competency-framework"
          className={`resto-section ${visibleSections.has("competency-framework") ? "resto-visible" : ""}`}
        >
          <div className="resto-section-content resto-fade-in-left">
            <div className="resto-section-label">Competencies</div>
            <h2 className="resto-section-title">
              {sectionContent["competency-framework"].title}
            </h2>
            <p className="resto-section-description">
              {sectionContent["competency-framework"].description}
            </p>

            <div className="resto-buttons-group">
              <button className="resto-primary-btn" onClick={handlePerformance}>Define Competencies</button>
              <button className="resto-secondary-btn" onClick={handlePerformance}>Assess Skills</button>
            </div>
          </div>

          <div className="resto-section-image resto-fade-in-right">
            <img src="/images/prm3.png" className="resto-real-image" alt="Competency Framework" />
          </div>
        </section>

        {/* 4. Continuous Feedback */}
        <section
          ref={(el) => (sectionsRef.current["continuous-feedback"] = el)}
          data-section="continuous-feedback"
          className={`resto-section resto-reverse ${visibleSections.has("continuous-feedback") ? "resto-visible" : ""}`}
        >
          <div className="resto-section-content resto-fade-in-right">
            <div className="resto-section-label">Feedback</div>
            <h2 className="resto-section-title">
              {sectionContent["continuous-feedback"].title}
            </h2>
            <p className="resto-section-description">
              {sectionContent["continuous-feedback"].description}
            </p>

            <div className="resto-buttons-group">
              <button className="resto-primary-btn" onClick={handlePerformance}>Give Feedback</button>
              <button className="resto-secondary-btn" onClick={handlePerformance}>Recognize Peers</button>
            </div>
          </div>

          <div className="resto-section-image resto-fade-in-left">
            <img src="/images/prm4.png" className="resto-real-image" alt="Continuous Feedback" />
          </div>
        </section>

        {/* 5. Development Planning */}
        <section
          ref={(el) => (sectionsRef.current["development-planning"] = el)}
          data-section="development-planning"
          className={`resto-section ${visibleSections.has("development-planning") ? "resto-visible" : ""}`}
        >
          <div className="resto-section-content resto-fade-in-left">
            <div className="resto-section-label">Development</div>
            <h2 className="resto-section-title">
              {sectionContent["development-planning"].title}
            </h2>
            <p className="resto-section-description">
              {sectionContent["development-planning"].description}
            </p>

            <div className="resto-buttons-group">
              <button className="resto-primary-btn" onClick={handlePerformance}>Create IDP</button>
              <button className="resto-secondary-btn" onClick={handlePerformance}>Track Growth</button>
            </div>
          </div>

          <div className="resto-section-image resto-fade-in-right">
            <img src="/images/prm5.png" className="resto-real-image" alt="Development Planning" />
          </div>
        </section>

      </div>
    </div>
  );
};

export default PerformanceManagementModule;